import React, { useState, useEffect } from 'react';
import { Coins, X, RefreshCw, Trash2 } from 'lucide-react';
import useTokenWallet from '../hooks/useTokenWallet';

/**
 * TokenWalletWidget — system-wide floating wallet display.
 *
 * Architect directive (2026-05-27): every visitor gets a wallet. Crypto-ready
 * shape — when wallet rail moves on-chain, the same widget reads from a
 * different source without UI change.
 *
 * - Hidden when wallet has 0 codes (no clutter for first-time visitors)
 * - Floating top-right pill with token glyph + balance
 * - Click → expanded drawer listing every code with balance + refresh + remove
 */
const NUMBER_FMT = new Intl.NumberFormat('en-US');

export default function TokenWalletWidget() {
  const { entries, totalBalance, refresh, remove, clear } = useTokenWallet();
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Auto-refresh on first paint so stale balances self-heal
  useEffect(() => {
    if (entries.length > 0) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (entries.length === 0) return null;

  const handleRefresh = async () => {
    setRefreshing(true);
    await refresh();
    setRefreshing(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="token-wallet-trigger"
        aria-label={`Open token wallet — ${totalBalance} tokens`}
        title={`${totalBalance} Federation Tokens across ${entries.length} code${entries.length === 1 ? '' : 's'}`}
        style={{
          position: 'fixed',
          top: '88px',
          right: '20px',
          zIndex: 9500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          borderRadius: '999px',
          background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.18), rgba(217, 119, 6, 0.20))',
          border: '1px solid rgba(251, 191, 36, 0.55)',
          color: '#fde68a',
          cursor: 'pointer',
          fontFamily: 'ui-monospace, Menlo, monospace',
          fontSize: '13px',
          fontWeight: 700,
          boxShadow: '0 0 16px rgba(251, 191, 36, 0.25)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Coins className="w-4 h-4" />
        <span data-testid="token-wallet-total">{NUMBER_FMT.format(totalBalance)}</span>
        <span style={{ fontSize: '10px', opacity: 0.7, letterSpacing: '0.1em' }}>TOKENS</span>
      </button>

      {open && (
        <div
          data-testid="token-wallet-drawer"
          role="dialog"
          aria-label="Federation Token Wallet"
          style={{
            position: 'fixed',
            top: '140px',
            right: '20px',
            zIndex: 9501,
            width: '360px',
            maxWidth: 'calc(100vw - 40px)',
            maxHeight: 'calc(100vh - 200px)',
            background: 'linear-gradient(180deg, #0a0a0a 0%, #050b14 100%)',
            border: '1px solid rgba(251, 191, 36, 0.45)',
            borderRadius: '14px',
            boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 0 30px rgba(251, 191, 36, 0.18)',
            color: '#e5e7eb',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '14px 16px',
              borderBottom: '1px solid rgba(251, 191, 36, 0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            <Coins className="w-5 h-5" style={{ color: '#fbbf24' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#fde68a' }}>
                Federation Token Wallet
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                {entries.length} code{entries.length === 1 ? '' : 's'} · 1 Token = $5 · Spendable on
                any course
              </div>
            </div>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="token-wallet-refresh"
              aria-label="Refresh balances"
              title="Refresh balances from server"
              style={{
                background: 'rgba(255,255,255,0.08)',
                color: '#cbd5e1',
                border: 'none',
                width: '30px',
                height: '30px',
                borderRadius: '8px',
                cursor: refreshing ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RefreshCw
                className="w-4 h-4"
                style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }}
              />
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="token-wallet-close"
              aria-label="Close wallet"
              style={{
                background: 'rgba(255,255,255,0.08)',
                color: '#cbd5e1',
                border: 'none',
                width: '30px',
                height: '30px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Total */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(251, 191, 36, 0.15)' }}>
            <div style={{ fontSize: '10px', letterSpacing: '0.14em', color: '#9ca3af', textTransform: 'uppercase' }}>
              Total Balance
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '4px' }}>
              <span
                style={{
                  fontSize: '32px',
                  fontWeight: 700,
                  color: '#fbbf24',
                  textShadow: '0 0 18px rgba(251, 191, 36, 0.45)',
                  fontFamily: 'ui-monospace, Menlo, monospace',
                }}
              >
                {NUMBER_FMT.format(totalBalance)}
              </span>
              <span style={{ color: '#9ca3af', fontSize: '13px' }}>
                tokens · {NUMBER_FMT.format(totalBalance * 5)} USD value
              </span>
            </div>
          </div>

          {/* Codes list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {entries.map((e) => (
              <div
                key={e.code}
                data-testid={`wallet-entry-${e.code}`}
                style={{
                  padding: '10px 12px',
                  margin: '4px 0',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontFamily: 'ui-monospace, Menlo, monospace',
                      fontSize: '12px',
                      color: '#22d3ee',
                      letterSpacing: '0.06em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {e.code}
                  </div>
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                    {e.value > 0
                      ? `${e.balance}/${e.value} tokens left · source: ${e.source}`
                      : `${e.balance} tokens · source: ${e.source}`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(e.code)}
                  aria-label={`Remove code ${e.code} from wallet`}
                  title="Remove from this device"
                  style={{
                    background: 'transparent',
                    color: '#6b7280',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '10px 16px',
              borderTop: '1px solid rgba(251, 191, 36, 0.15)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '11px',
              color: '#9ca3af',
            }}
          >
            <span>Local to this device · syncs to account when you sign in</span>
            <button
              type="button"
              onClick={clear}
              data-testid="token-wallet-clear"
              style={{
                background: 'transparent',
                color: '#9ca3af',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '11px',
              }}
            >
              Clear all
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
